import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { fetchWithTimeout } from '../utils/fetchWithTimeout'
import { SectionVueMarche } from './analyse/SectionVueMarche'
import { SectionTendancesModeles } from './analyse/SectionTendancesModeles'
import { SectionComparaisonSegments } from './analyse/SectionComparaisonSegments'
import { SectionBenchmarkingConcession } from './analyse/SectionBenchmarkingConcession'

import { BACKEND_URL as BACKEND } from '../config/backendUrl'
const LS_TOUT_MARCHE = 'radar_analyse_tout_marche'

function readToutMarche() {
  try {
    return localStorage.getItem(LS_TOUT_MARCHE) === '1'
  } catch {
    return false
  }
}

function writeToutMarche(v) {
  try {
    localStorage.setItem(LS_TOUT_MARCHE, v ? '1' : '0')
  } catch {}
}

function useAnalyseSection(token, path, toutMarche, refreshKey) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(
    async (refresh = false) => {
      if (!token) return
      setLoading(true)
      setError(null)
      try {
        const q = new URLSearchParams()
        if (toutMarche) q.set('tout_marche', '1')
        if (refresh) q.set('refresh', '1')
        const url = `${BACKEND}/api/analyse/${path}${q.toString() ? `?${q}` : ''}`
        const res = await fetchWithTimeout(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || `Erreur ${res.status}`)
        setData(json)
      } catch (e) {
        setError(e.message || 'Erreur de chargement')
        setData(null)
      } finally {
        setLoading(false)
      }
    },
    [token, path, toutMarche]
  )

  useEffect(() => {
    load(false)
  }, [load, refreshKey])

  return { data, loading, error, reload: () => load(true) }
}

export function AnalyseTab({ token, user }) {
  const [toutMarche, setToutMarche] = useState(readToutMarche)
  const [refreshKey, setRefreshKey] = useState(0)

  const vue = useAnalyseSection(token, 'vue-marche', toutMarche, refreshKey)
  const tendances = useAnalyseSection(token, 'tendances-modeles', toutMarche, refreshKey)
  const segments = useAnalyseSection(token, 'comparaison-segments', toutMarche, refreshKey)
  const bench = useAnalyseSection(token, 'benchmarking', toutMarche, refreshKey)

  const onToggleMarche = () => {
    setToutMarche((prev) => {
      const next = !prev
      writeToutMarche(next)
      return next
    })
    setRefreshKey((k) => k + 1)
  }

  const refreshAll = () => {
    setRefreshKey((k) => k + 1)
    vue.reload()
    tendances.reload()
    segments.reload()
    bench.reload()
  }

  const marquesLabel =
    toutMarche || !user?.marques_interet?.length
      ? 'Tout le marché suisse'
      : user.marques_interet.map((m) => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: '28px 32px 48px',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <header
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 20,
          marginBottom: 36,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 300, color: '#fff', margin: 0 }}>Analyse</h1>
          <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 8, maxWidth: 520 }}>
            Pilotage stratégique — tendances marché, segments et positionnement de votre concession.
          </p>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Périmètre : {marquesLabel}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 12,
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={toutMarche}
              onChange={onToggleMarche}
              style={{ accentColor: '#001E50' }}
            />
            Voir tout le marché suisse
          </label>
          <button
            type="button"
            onClick={refreshAll}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              fontSize: 12,
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.03)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} aria-hidden />
            Actualiser
          </button>
        </div>
      </header>

      <SectionVueMarche data={vue.data} loading={vue.loading} error={vue.error} />
      <SectionTendancesModeles data={tendances.data} loading={tendances.loading} error={tendances.error} />
      <SectionComparaisonSegments data={segments.data} loading={segments.loading} error={segments.error} />
      <SectionBenchmarkingConcession data={bench.data} loading={bench.loading} error={bench.error} />
    </div>
  )
}
